import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Printer, Download, Barcode, QrCode, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { generateDocumentCode, generateBundleCode, generateDocumentCodesBulk, generateBundleCodesBulk } from '@/services/api';
import PrintDialog from '@/components/PrintDialog';

const PrintCodes = () => {
  const navigate = useNavigate();
  
  // Document codes state
  const [docCodes, setDocCodes] = useState([]);
  const [docQuantity, setDocQuantity] = useState(1000);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  
  // Bundle codes state
  const [bundleCodes, setBundleCodes] = useState([]);
  const [bundleQuantity, setBundleQuantity] = useState(1000);
  const [isGeneratingBundle, setIsGeneratingBundle] = useState(false);

  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCodes, setPrintCodes] = useState([]);
  const [printLabelType, setPrintLabelType] = useState('document');

  const handleGenerateDocumentCodes = async () => {
    if (docQuantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    if (docQuantity > 10000) {
      toast.error('Maximum 10000 codes per batch');
      return;
    }

    setIsGeneratingDoc(true);
    try {
      toast.info(`Generating ${docQuantity} sequential document codes...`);
      
      // Call bulk generation endpoint - generates all codes atomically
      const result = await generateDocumentCodesBulk(docQuantity);
      
      const newCodes = result.codes.map(codeData => ({
        code: codeData.code,
        type: 'barcode',
        generated_at: new Date().toISOString()
      }));
      
      setDocCodes(newCodes);
      
      toast.success(
        <div>
          <p className="font-semibold">Generated {docQuantity} codes successfully!</p>
          <p className="text-sm mt-1">Range: {result.start_code} to {result.end_code}</p>
        </div>,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error generating document codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate document codes. Please try again.');
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const handleGenerateBundleCodes = async () => {
    if (bundleQuantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    if (bundleQuantity > 10000) {
      toast.error('Maximum 10000 codes per batch');
      return;
    }

    setIsGeneratingBundle(true);
    try {
      toast.info(`Generating ${bundleQuantity} sequential bundle codes...`);
      
      // Call bulk generation endpoint - generates all codes atomically
      const result = await generateBundleCodesBulk(bundleQuantity);
      
      const newCodes = result.codes.map(codeData => ({
        code: codeData.code,
        type: 'qrcode',
        generated_at: new Date().toISOString()
      }));
      
      setBundleCodes(newCodes);
      
      toast.success(
        <div>
          <p className="font-semibold">Generated {bundleQuantity} codes successfully!</p>
          <p className="text-sm mt-1">Range: {result.start_code} to {result.end_code}</p>
        </div>,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error generating bundle codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate bundle codes. Please try again.');
    } finally {
      setIsGeneratingBundle(false);
    }
  };

  const handlePrintAllCodes = (codes, type) => {
    if (codes.length === 0) {
      toast.error('No codes to print');
      return;
    }

    // Extract code strings and open print dialog
    const codeStrings = codes.map(c => c.code);
    setPrintCodes(codeStrings);
    setPrintLabelType(type === 'barcode' ? 'document' : 'bundle');
    setShowPrintDialog(true);
  };

  const handleExportCSV = (codes, filename) => {
    if (codes.length === 0) {
      toast.error('No codes to export');
      return;
    }

    try {
      // Create CSV content
      const headers = ['Sr No', 'Code', 'Type', 'Generated At', 'Fiscal Year'];
      const rows = codes.map((code, index) => [
        index + 1,
        code.code,
        code.type === 'barcode' ? 'Document Barcode' : 'Bundle QR Code',
        new Date(code.generated_at).toLocaleString('en-IN'),
        code.code.split('/')[1] || '25-26'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${codes.length} codes to ${filename}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV file');
    }
  };

  // CodeTable component is moved inline to avoid nested component issue
  const renderCodeTable = (codes, type, currentPage, setCurrentPage) => {
    const itemsPerPage = 100;
    const totalPages = Math.ceil(codes.length / itemsPerPage);
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentCodes = codes.slice(startIndex, endIndex);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5A5F6E]">
            Showing {startIndex + 1} - {Math.min(endIndex, codes.length)} of {codes.length} codes
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[#5A5F6E]">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide w-24">
                    Sr No
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Code
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Type
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Generated At
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentCodes.map((code, index) => (
                  <TableRow
                    key={`${code.code}-${index}`}
                    className="border-b border-[#E5DFD0] hover:bg-[#FFFBF5] transition-colors duration-150"
                  >
                    <TableCell className="font-medium text-[#1A1F2E]">
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-[#1A1F2E] font-medium">
                      {code.code}
                    </TableCell>
                    <TableCell className="text-sm text-[#5A5F6E]">
                      {type === 'barcode' ? 'Document Barcode' : 'Bundle QR Code'}
                    </TableCell>
                    <TableCell className="text-sm text-[#5A5F6E]">
                      {new Date(code.generated_at).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  // Pagination state for tables
  const [docPage, setDocPage] = useState(1);
  const [bundlePage, setBundlePage] = useState(1);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Bulk Print Codes</h1>
              <p className="text-sm text-[#E8DCC8]">Generate & Print Barcodes/QR Codes in Bulk</p>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/printer-setup')}
            className="bg-white/10 text-white hover:bg-white/20"
          >
            <Printer className="w-4 h-4 mr-2" />
            Printer Setup
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Tabs defaultValue="document" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2 h-12">
            <TabsTrigger value="document" className="text-base" data-testid="document-tab">
              <Barcode className="w-5 h-5 mr-2" />
              Document Barcodes
            </TabsTrigger>
            <TabsTrigger value="bundle" className="text-base" data-testid="bundle-tab">
              <QrCode className="w-5 h-5 mr-2" />
              Bundle QR Codes
            </TabsTrigger>
          </TabsList>

          {/* Document Barcodes Tab */}
          <TabsContent value="document" className="space-y-6">
            <Card className="p-6 bg-white border-2 border-[#E5DFD0]">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                Generate Document Barcodes (Bulk)
              </h2>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="doc-quantity" className="text-sm font-medium text-[#1A1F2E]">
                      Quantity *
                    </Label>
                    <Input
                      id="doc-quantity"
                      type="number"
                      min="1"
                      max="10000"
                      value={docQuantity}
                      onChange={(e) => setDocQuantity(parseInt(e.target.value) || 1)}
                      className="mt-1 h-12"
                      disabled={isGeneratingDoc}
                      data-testid="doc-quantity-input"
                    />
                    <p className="text-xs text-[#8A8F9E] mt-1">Minimum 1, Maximum 10000 codes per batch</p>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleGenerateDocumentCodes}
                      disabled={isGeneratingDoc || docQuantity < 1}
                      className="h-12 px-6 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                      data-testid="generate-doc-codes-button"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      {isGeneratingDoc ? 'Generating...' : `Generate ${docQuantity} Codes`}
                    </Button>
                  </div>
                </div>

                {docCodes.length > 0 && (
                  <div className="flex gap-3 pt-4 border-t border-[#E5DFD0]">
                    <Button
                      onClick={() => handlePrintAllCodes(docCodes, 'barcode')}
                      className="flex-1 h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                      data-testid="print-all-doc-button"
                    >
                      <Printer className="w-5 h-5 mr-2" />
                      Print All {docCodes.length} Codes
                    </Button>
                    <Button
                      onClick={() => handleExportCSV(docCodes, `document_barcodes_${Date.now()}.csv`)}
                      variant="secondary"
                      className="flex-1 h-12 border-2 border-[#E5DFD0]"
                      data-testid="export-doc-csv-button"
                    >
                      <FileDown className="w-5 h-5 mr-2" />
                      Export to CSV
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Generated Document Codes Table */}
            {docCodes.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                  Generated Codes ({docCodes.length})
                </h3>
                {renderCodeTable(docCodes, 'barcode', docPage, setDocPage)}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-[#E5DFD0] rounded-lg">
                <Barcode className="w-16 h-16 mx-auto text-[#8A8F9E] mb-3" />
                <p className="text-[#5A5F6E]">No document codes generated yet</p>
                <p className="text-sm text-[#8A8F9E] mt-1">Enter quantity and click generate to start</p>
              </div>
            )}
          </TabsContent>

          {/* Bundle QR Codes Tab */}
          <TabsContent value="bundle" className="space-y-6">
            <Card className="p-6 bg-white border-2 border-[#E5DFD0]">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                Generate Bundle QR Codes (Bulk)
              </h2>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="bundle-quantity" className="text-sm font-medium text-[#1A1F2E]">
                      Quantity *
                    </Label>
                    <Input
                      id="bundle-quantity"
                      type="number"
                      min="1"
                      max="10000"
                      value={bundleQuantity}
                      onChange={(e) => setBundleQuantity(parseInt(e.target.value) || 1)}
                      className="mt-1 h-12"
                      disabled={isGeneratingBundle}
                      data-testid="bundle-quantity-input"
                    />
                    <p className="text-xs text-[#8A8F9E] mt-1">Minimum 1, Maximum 10000 codes per batch</p>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleGenerateBundleCodes}
                      disabled={isGeneratingBundle || bundleQuantity < 1}
                      className="h-12 px-6 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                      data-testid="generate-bundle-codes-button"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      {isGeneratingBundle ? 'Generating...' : `Generate ${bundleQuantity} Codes`}
                    </Button>
                  </div>
                </div>

                {bundleCodes.length > 0 && (
                  <div className="flex gap-3 pt-4 border-t border-[#E5DFD0]">
                    <Button
                      onClick={() => handlePrintAllCodes(bundleCodes, 'qrcode')}
                      className="flex-1 h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                      data-testid="print-all-bundle-button"
                    >
                      <Printer className="w-5 h-5 mr-2" />
                      Print All {bundleCodes.length} Codes
                    </Button>
                    <Button
                      onClick={() => handleExportCSV(bundleCodes, `bundle_qrcodes_${Date.now()}.csv`)}
                      variant="secondary"
                      className="flex-1 h-12 border-2 border-[#E5DFD0]"
                      data-testid="export-bundle-csv-button"
                    >
                      <FileDown className="w-5 h-5 mr-2" />
                      Export to CSV
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Generated Bundle Codes Table */}
            {bundleCodes.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                  Generated Codes ({bundleCodes.length})
                </h3>
                {renderCodeTable(bundleCodes, 'qrcode', bundlePage, setBundlePage)}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-[#E5DFD0] rounded-lg">
                <QrCode className="w-16 h-16 mx-auto text-[#8A8F9E] mb-3" />
                <p className="text-[#5A5F6E]">No bundle codes generated yet</p>
                <p className="text-sm text-[#8A8F9E] mt-1">Enter quantity and click generate to start</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="mt-8 p-6 bg-white border border-[#E5DFD0]">
          <h3 className="text-lg font-semibold text-[#1A1F2E] mb-3 font-display">Bulk Printing Instructions</h3>
          <ul className="space-y-2 text-[#5A5F6E] text-sm">
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>Enter desired quantity (minimum 1, recommended 1000-5000 per batch)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>Click <strong>&ldquo;Generate X Codes&rdquo;</strong> to create sequential codes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>Click <strong>&ldquo;Print All Codes&rdquo;</strong> to send to your thermal printer</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>Click <strong>&ldquo;Export to CSV&rdquo;</strong> to download codes for record keeping</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>All codes are sequential and unique (DOCXXXXXX/25-26, BUNXXXXXX/25-26)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-0.5">•</span>
              <span>Generated codes are automatically registered in the system</span>
            </li>
          </ul>
        </Card>
      </main>

      {/* Print Dialog */}
      <PrintDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        codes={printCodes}
        labelType={printLabelType}
        operationKey="print-codes"
        title={`Print ${printLabelType === 'document' ? 'Document' : 'Bundle'} Labels`}
      />
    </div>
  );
};

export default PrintCodes;
